import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  FormGroup,
  Label,
  Form,
} from 'reactstrap';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { API_URL } from '../../utils/constant';
import { toastr } from 'react-redux-toastr';
import Pagination from '../Pagination/Pagination';


const animatedComponents = makeAnimated();

const ScanLog = () => {
  const [selectedScan, setSelectedScan] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showAddScanModal, setShowAddScanModal] = useState(false);
  const [scanType, setScanType] = useState('single');
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [scanMode, setScanMode] = useState('full');
  const [selectedTag, setSelectedTag] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedTargets, setExpandedTargets] = useState({}); // scanId -> bool for table rows
  const [modalShowAllTargets, setModalShowAllTargets] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({}); // scanId -> bool for dropdown visibility
  const [modalDropdownOpen, setModalDropdownOpen] = useState(false); // modal dropdown visibility

  const orgId = useSelector((state) => state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id);

  useEffect(() => {
    if (orgId) {
      fetchScanLogs();
    }
  }, [orgId, currentPage, itemsPerPage]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close table row dropdowns
      Object.keys(dropdownOpen).forEach(scanId => {
        if (dropdownOpen[scanId]) {
          const dropdownElement = document.querySelector(`[data-scan-id="${scanId}"]`);
          if (dropdownElement && !dropdownElement.contains(event.target)) {
            setDropdownOpen(prev => ({
              ...prev,
              [scanId]: false
            }));
          }
        }
      });

      // Close modal dropdown
      if (modalDropdownOpen) {
        const modalDropdownElement = document.querySelector('[data-modal-dropdown]');
        if (modalDropdownElement && !modalDropdownElement.contains(event.target)) {
          setModalDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, modalDropdownOpen]);

  const fetchScanLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/get-scan-logs/${orgId}?limit=${itemsPerPage}&page=${currentPage}`);
      if (response.data && !response.data.error) {
        const formattedScans = response.data.data.map(scan => ({
          id: scan._id,
          target: scan.targets?.[0] || 'Unknown Target',
          targets: Array.isArray(scan.targets) ? scan.targets : (scan.targets ? [scan.targets] : []),
          type: scan.targetType || 'Unknown Type',
          startTime: new Date(scan.createdAt).toLocaleString(),
          endTime: scan.completedAt ? new Date(scan.completedAt).toLocaleString() : null,
          status: scan.isCompleted ? 'Completed' : 'In Progress',
          progress: scan.progress || 0,
          scanMode: scan.targetType || 'Unknown Mode',
          tags: scan.tags || [],
          issues: {
            critical: scan.scanStats?.totalCritical || 0,
            high: scan.scanStats?.totalHigh || 0,
            medium: scan.scanStats?.totalMedium || 0,
            low: scan.scanStats?.totalLow || 0,
            info: scan.scanStats?.totalInfo || 0
          }
        }));
        setScans(formattedScans);
        setTotalItems(response.data.totalItems);
      }
    } catch (error) {
      // console.error removed
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'warning';
      case 'Failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const handleViewDetails = (scan) => {
    setSelectedScan(scan);
    setModalOpen(true);
  };

  const handleDownloadPDF = async (scan, summaryOnly = false) => {
    try {
      const session = localStorage.getItem("session");
      let url = `${API_URL}/organisations/${orgId}/av/scans/${scan.id}/report/pdf`;
      if (summaryOnly) {
        url += '?summaryOnly=true';
      }

      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/pdf',
          'Authorization': `Bearer ${session}`
        }
      });

      // Create blob link to download
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      const fileName = summaryOnly ? `scan-summary-${scan.target}-${scan.id}.pdf` : `scan-report-${scan.target}-${scan.id}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      const errorMessage = summaryOnly ? 'Failed to download summary report' : 'Failed to download PDF report';
      toastr.error('Error', errorMessage);
    }
  };

  const handleDownloadCSV = async (scan) => {
    try {
      const response = await axios.get(
        `${API_URL}/organisations/${orgId}/av/scans/${scan.id}/report/csv`,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'text/csv'
          }
        }
      );
      console.log('CSVvvv', response);
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `scan-report-${scan.target}-${scan.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV report:', error);
      toastr.error('Error', 'Failed to download CSV report');
    }
  };

  const toggleDropdown = (scanId) => {
    setDropdownOpen(prev => ({
      ...prev,
      [scanId]: !prev[scanId]
    }));
  };

  const handleExportOption = (scan, option) => {
    setDropdownOpen(prev => ({
      ...prev,
      [scan.id]: false
    }));

    switch (option) {
      case 'full':
        handleDownloadPDF(scan);
        break;
      case 'executive':
        handleDownloadPDF(scan, true);
        break;
      case 'csv':
        handleDownloadCSV(scan);
        break;
      default:
        break;
    }
  };

  const handleModalExportOption = (option) => {
    setModalDropdownOpen(false);

    switch (option) {
      case 'full':
        handleDownloadPDF(selectedScan);
        break;
      case 'executive':
        handleDownloadPDF(selectedScan, true);
        break;
      case 'csv':
        handleDownloadCSV(selectedScan);
        break;
      default:
        break;
    }
  };

  const handleStartScan = () => {
    // Handle scan initiation logic here
    setShowAddScanModal(false);
  };

  const filteredScans = selectedTag
    ? scans.filter(scan => scan.tags.includes(selectedTag.value))
    : scans;

  if (loading) {
    return (
      <div style={{ padding: '1.5rem', textAlign: 'center' }}>
        <p>Loading scan logs...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{
              margin: 0,
              color: '#2c3e50',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>Scan Logs</h3>
            <p style={{
              margin: '0.5rem 0 0',
              color: '#6c757d',
              fontSize: '0.9rem'
            }}>Monitor and manage your security scans</p>
          </div>
        </div>

        <div style={{ padding: '1.5rem' }}>


          <div style={{
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #eee'
            }}>
              <h5 style={{
                margin: 0,
                color: '#2c3e50',
                fontWeight: '600'
              }}>Active Scans</h5>
            </div>
            <div style={{ padding: 0 }}>
              <div style={{ overflowX: 'auto' }} className='table-responsive'>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{
                      background: '#f8f9fa',
                      borderBottom: '2px solid #eee'
                    }}>
                      <th style={{ padding: '1rem' }}>Targets</th>
                      <th style={{ padding: '1rem' }}>Type</th>
                      <th style={{ padding: '1rem' }}>Start Time</th>
                      <th style={{ padding: '1rem' }}>End Time</th>
                      <th style={{ padding: '1rem' }}>Status</th>
                      <th style={{ padding: '1rem' }}>Progress</th>
                      <th style={{ padding: '1rem' }}>Tags</th>
                      <th style={{ padding: '1rem' }}>Issues</th>
                      <th style={{ padding: '1rem' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredScans.map((scan) => (
                      <tr key={scan.id} style={{
                        borderBottom: '1px solid #eee',
                        '&:hover': {
                          background: '#f8f9fa'
                        }
                      }}>
                        <td style={{ padding: '1rem', maxWidth: '320px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                            {(expandedTargets[scan.id] ? scan.targets : scan.targets.slice(0, 3)).map((t, idx) => (
                              <span key={t + idx} style={{
                                background: '#f1f3f5',
                                color: '#334155',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                border: '1px solid #e5e7eb'
                              }}>
                                {t}
                              </span>
                            ))}
                            {scan.targets.length > 3 && !expandedTargets[scan.id] && (
                              <span style={{
                                background: '#e9ecef',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                border: '1px solid #e5e7eb'
                              }}>
                                +{scan.targets.length - 3} more
                              </span>
                            )}
                          </div>
                          {scan.targets.length > 3 && (
                            <button
                              onClick={() => setExpandedTargets(prev => ({ ...prev, [scan.id]: !prev[scan.id] }))}
                              style={{
                                background: 'transparent',
                                color: '#453acd',
                                border: 'none',
                                padding: 0,
                                marginTop: '0.25rem',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                            >
                              Show {expandedTargets[scan.id] ? 'less' : 'more'}
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '1rem' }}>{scan.type}</td>
                        <td style={{ padding: '1rem' }}>{scan.startTime}</td>
                        <td style={{ padding: '1rem' }}>{scan.endTime || '-'}</td>
                        <td style={{ padding: '1rem', width: '150px' }}>
                          <span style={{
                            background: getStatusColor(scan.status) === 'success' ? '#28a745' :
                              getStatusColor(scan.status) === 'warning' ? '#ffc107' :
                                getStatusColor(scan.status) === 'danger' ? '#dc3545' : '#6c757d',
                            color: getStatusColor(scan.status) === 'warning' ? '#000' : '#fff',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }}>
                            {scan.status}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            background: '#e9ecef',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${scan.progress}%`,
                              height: '100%',
                              background: getStatusColor(scan.status) === 'success' ? '#28a745' :
                                getStatusColor(scan.status) === 'warning' ? '#ffc107' :
                                  getStatusColor(scan.status) === 'danger' ? '#dc3545' : '#6c757d',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                          <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                            {scan.progress}%
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {scan.tags.map(tag => (
                              <span key={tag} style={{
                                background: '#08283f',
                                color: '#fff',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {scan.issues.critical > 0 && (
                              <span style={{
                                background: '#dc3545',
                                color: '#fff',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}>
                                {scan.issues.critical}
                              </span>
                            )}
                            {scan.issues.high > 0 && (
                              <span style={{
                                background: '#ffc107',
                                color: '#000',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}>
                                {scan.issues.high}
                              </span>
                            )}
                            {scan.issues.medium > 0 && (
                              <span style={{
                                background: '#17a2b8',
                                color: '#fff',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}>
                                {scan.issues.medium}
                              </span>
                            )}
                            {scan.issues.low > 0 && (
                              <span style={{
                                background: '#6c757d',
                                color: '#fff',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}>
                                {scan.issues.low}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '1rem', display: 'flex', width: '400px' }}>
                          <button
                            style={{
                              background: '#08283f',
                              color: '#fff',
                              border: 'none',
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => handleViewDetails(scan)}
                          >
                            <i className="fa fa-eye"></i>
                            View Details
                          </button>
                          {scan.status !== 'In Progress' && (
                            <div style={{ position: 'relative', marginLeft: '0.5rem' }} data-scan-id={scan.id}>
                              <button
                                style={{
                                  background: '#6c757d',
                                  color: '#fff',
                                  border: 'none',
                                  padding: '0.5rem 1rem',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  transition: 'all 0.2s'
                                }}
                                onClick={() => toggleDropdown(scan.id)}
                              >
                                <i className="fa fa-download"></i>
                                Export Report
                                <i className="fa fa-caret-down" style={{ marginLeft: '0.25rem' }}></i>
                              </button>

                              {dropdownOpen[scan.id] && (
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: '0',
                                  background: '#fff',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '6px',
                                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                  zIndex: 1000,
                                  minWidth: '180px',
                                  marginTop: '0.25rem'
                                }}>
                                  <button
                                    style={{
                                      width: '100%',
                                      padding: '0.75rem 1rem',
                                      border: 'none',
                                      background: 'transparent',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      transition: 'background-color 0.2s',
                                      color: '#333'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                    onClick={() => handleExportOption(scan, 'full')}
                                  >
                                    <i className="fa fa-file-pdf-o" style={{ color: '#dc3545' }}></i>
                                    Full Report
                                  </button>
                                  <button
                                    style={{
                                      width: '100%',
                                      padding: '0.75rem 1rem',
                                      border: 'none',
                                      background: 'transparent',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      transition: 'background-color 0.2s',
                                      color: '#333'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                    onClick={() => handleExportOption(scan, 'executive')}
                                  >
                                    <i className="fa fa-file-text-o" style={{ color: '#17a2b8' }}></i>
                                    Executive Summary
                                  </button>
                                  <button
                                    style={{
                                      width: '100%',
                                      padding: '0.75rem 1rem',
                                      border: 'none',
                                      background: 'transparent',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      transition: 'background-color 0.2s',
                                      color: '#333'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                    onClick={() => handleExportOption(scan, 'csv')}
                                  >
                                    <i className="fa fa-file-excel-o" style={{ color: '#28a745' }}></i>
                                    CSV Export
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Updated pagination controls with custom Pagination component */}
            <div className="d-flex justify-content-between align-content-center" style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid #eee'
            }}>
              <div className="dataTables_paginate">
                {totalItems > 0 && (
                  <Pagination
                    className="pagination-bar"
                    currentPage={currentPage}
                    totalCount={totalItems}
                    pageSize={itemsPerPage}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                  />
                )}
              </div>
              {totalItems > 10 && (
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="form-control limitSelectBox"
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          Scan Details - {selectedScan?.target}
        </ModalHeader>
        <ModalBody>
          {selectedScan && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <h5>Scan Information</h5>
                  <Table borderless>
                    <tbody>
                      <tr>
                        <td><strong>Target:</strong></td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                            {(modalShowAllTargets ? selectedScan.targets : selectedScan.targets.slice(0, 6)).map((t, idx) => (
                              <span key={t + idx} style={{
                                background: '#f1f3f5',
                                color: '#334155',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                border: '1px solid #e5e7eb'
                              }}>
                                {t}
                              </span>
                            ))}
                            {selectedScan.targets?.length > 6 && !modalShowAllTargets && (
                              <span style={{
                                background: '#e9ecef',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                border: '1px solid #e5e7eb'
                              }}>
                                +{selectedScan.targets.length - 6} more
                              </span>
                            )}
                          </div>
                          {selectedScan.targets?.length > 6 && (
                            <div>
                              <button
                                onClick={() => setModalShowAllTargets(v => !v)}
                                style={{
                                  background: 'transparent',
                                  color: '#453acd',
                                  border: 'none',
                                  padding: 0,
                                  marginTop: '0.25rem',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem'
                                }}
                              >
                                Show {modalShowAllTargets ? 'less' : 'more'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Type:</strong></td>
                        <td>{selectedScan.type}</td>
                      </tr>
                      <tr>
                        <td><strong>Start Time:</strong></td>
                        <td>{selectedScan.startTime}</td>
                      </tr>
                      <tr>
                        <td><strong>End Time:</strong></td>
                        <td>{selectedScan.endTime || '-'}</td>
                      </tr>
                      <tr>
                        <td><strong>Status:</strong></td>
                        <td>
                          <Badge color={getStatusColor(selectedScan.status)}>
                            {selectedScan.status}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Tags:</strong></td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {selectedScan.tags.map(tag => (
                              <span key={tag} style={{
                                background: '#08283f',
                                color: '#fff',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
                <Col md={6}>
                  <h5>Issue Summary</h5>
                  <Table borderless>
                    <tbody>
                      <tr>
                        <td><strong>Critical:</strong></td>
                        <td><Badge color="danger">{selectedScan.issues.critical}</Badge></td>
                      </tr>
                      <tr>
                        <td><strong>High:</strong></td>
                        <td><Badge color="warning">{selectedScan.issues.high}</Badge></td>
                      </tr>
                      <tr>
                        <td><strong>Medium:</strong></td>
                        <td><Badge color="info">{selectedScan.issues.medium}</Badge></td>
                      </tr>
                      <tr>
                        <td><strong>Low:</strong></td>
                        <td><Badge color="secondary">{selectedScan.issues.low}</Badge></td>
                      </tr>
                      <tr>
                        <td><strong>Info:</strong></td>
                        <td><Badge color="secondary">{selectedScan.issues.info}</Badge></td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {selectedScan && selectedScan.status !== 'In Progress' && (
            <div style={{ position: 'relative', display: 'inline-block' }} data-modal-dropdown>
              <Button
                color="secondary"
                onClick={() => setModalDropdownOpen(!modalDropdownOpen)}
                style={{ marginRight: '0.5rem' }}
              >
                <i className="fa fa-download mr-1"></i> Export Report
                <i className="fa fa-caret-down ml-1"></i>
              </Button>

              {modalDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  background: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  minWidth: '180px',
                  marginTop: '0.25rem'
                }}>
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'background-color 0.2s',
                      color: '#333'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    onClick={() => handleModalExportOption('full')}
                  >
                    <i className="fa fa-file-pdf-o" style={{ color: '#dc3545' }}></i>
                    Full Report
                  </button>
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'background-color 0.2s',
                      color: '#333'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    onClick={() => handleModalExportOption('executive')}
                  >
                    <i className="fa fa-file-text-o" style={{ color: '#17a2b8' }}></i>
                    Executive Summary
                  </button>
                  <button
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'background-color 0.2s',
                      color: '#333'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    onClick={() => handleModalExportOption('csv')}
                  >
                    <i className="fa fa-file-excel-o" style={{ color: '#28a745' }}></i>
                    CSV Export
                  </button>
                </div>
              )}
            </div>
          )}
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* New Scan Modal */}
      <Modal isOpen={showAddScanModal} toggle={() => setShowAddScanModal(false)}>
        <ModalHeader toggle={() => setShowAddScanModal(false)}>
          Start New Scan
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Scan Type</Label>
              <select
                className="form-control"
                value={scanType}
                onChange={(e) => setScanType(e.target.value)}
              >
                <option value="single">Single Target</option>
                <option value="multiple">Multiple Targets</option>
                <option value="all">All Targets</option>
              </select>
            </FormGroup>

            {scanType !== 'all' && (
              <FormGroup>
                <Label>Select Target{scanType === 'multiple' ? 's' : ''}</Label>
                <Select
                  isMulti={scanType === 'multiple'}
                  components={animatedComponents}
                  options={Array.from(new Set(scans.flatMap(s => s.targets?.length ? s.targets : [s.target])))
                    .map(t => ({ value: t, label: t }))}
                  value={selectedTargets}
                  onChange={setSelectedTargets}
                  placeholder={`Select target${scanType === 'multiple' ? 's' : ''}...`}
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label>Scan Mode</Label>
              <select
                className="form-control"
                value={scanMode}
                onChange={(e) => setScanMode(e.target.value)}
              >
                <option value="full">Full Scan</option>
                <option value="quick">Quick Scan</option>
                <option value="vulnerability">Vulnerability Scan</option>
              </select>
            </FormGroup>

            <FormGroup>
              <Label>Tags</Label>
              <Select
                isMulti
                components={animatedComponents}
                options={scans.reduce((acc, scan) => {
                  scan.tags.forEach(tag => {
                    if (!acc.find(t => t.value === tag)) {
                      acc.push({ value: tag, label: tag });
                    }
                  });
                  return acc;
                }, [])}
                placeholder="Select tags..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowAddScanModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleStartScan}>
            Start Scan
          </Button>
        </ModalFooter>
      </Modal>


    </div>
  );
};

export default ScanLog; 